import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Alert,
  LinearProgress,
  InputAdornment
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  QrCodeScanner as QrCodeScannerIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { BoxCode } from './types';
import {
  validateBoxCodeFormat,
  findBoxByCode,
  getScanningProgress,
  areAllBoxesScanned
} from './qrGenerator';
import { convertRuToEn } from './keyboardMapping';
import { colors } from './DocumentLayout';


interface BoxScanningViewProps {
  boxes: BoxCode[];
  onBoxScanned: (boxNum: number, code: string) => void;
  onComplete: () => void;
  onBack: () => void;
  mode?: 'sending' | 'receiving';
  documentNumber?: string;
  fromPharmacy?: string;
  toPharmacy?: string;
  comment?: string;
  showFooter?: boolean;
}

const BoxScanningView: React.FC<BoxScanningViewProps> = ({
  boxes,
  onBoxScanned,
  onComplete,
  onBack,
  mode = 'sending',
  showFooter = true
}) => {
  const [scanInput, setScanInput] = useState('');
  const [error, setError] = useState<string>('');
  const [lastScanned, setLastScanned] = useState<number | null>(null);
  const [successFlash, setSuccessFlash] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Автозфокус
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // Возвращаем фокус после сканирования
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [lastScanned, error]);

  // Flash-эффект при успешном сканировании
  useEffect(() => {
    if (lastScanned !== null) {
      setSuccessFlash(true);
      const timer = setTimeout(() => setSuccessFlash(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [lastScanned]);

  const progress = getScanningProgress(boxes);
  const allScanned = areAllBoxesScanned(boxes);
  const progressPercent = progress.total > 0 ? (progress.scanned / progress.total) * 100 : 0;

  const handleScan = (code: string) => {
    if (!code.trim()) return;

    setError('');
    setLastScanned(null);

    const convertedCode = convertRuToEn(code);

    if (!validateBoxCodeFormat(convertedCode)) {
      setError('Неверный формат QR-кода');
      setScanInput('');
      return;
    }

    const foundBox = findBoxByCode(convertedCode, boxes);

    if (!foundBox) {
      setError('QR-код не найден в списке коробов');
      setScanInput('');
      return;
    }

    if (foundBox.scanned) {
      setError(`Короб №${foundBox.box_num} уже отсканирован`);
      setScanInput('');
      return;
    }

    onBoxScanned(foundBox.box_num, convertedCode);
    setLastScanned(foundBox.box_num);
    setScanInput('');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScan(scanInput);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = convertRuToEn(e.target.value);
    setScanInput(value);

    if (value.length >= 50) {
      setTimeout(() => handleScan(value), 100);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Зона сканирования */}
      <Box sx={{
        p: 2.5,
        bgcolor: successFlash ? 'success.50' : 'white',
        borderBottom: `1px solid ${colors.border}`,
        transition: 'background-color 0.3s ease'
      }}>
        <TextField
          fullWidth
          inputRef={inputRef}
          value={scanInput}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={allScanned ? 'Все коробы отсканированы' : 'Отсканируйте QR-код короба...'}
          variant="outlined"
          size="medium"
          autoFocus
          disabled={allScanned}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <QrCodeScannerIcon sx={{ color: allScanned ? 'success.main' : 'primary.main' }} />
              </InputAdornment>
            ),
            endAdornment: scanInput.length > 0 && (
              <InputAdornment position="end">
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                  {scanInput.length} сим.
                </Typography>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'monospace',
              fontSize: '1rem',
              bgcolor: allScanned ? '#f0fdf4' : '#fafbff',
              borderRadius: 2,
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: 2
              }
            }
          }}
        />



        {/* Успешное сканирование */}
        {successFlash && lastScanned !== null && (
          <Alert
            severity="success"
            sx={{ mt: 1.5, borderRadius: 2 }}
            icon={<CheckCircleIcon />}
          >
            Короб №{lastScanned} отсканирован
          </Alert>
        )}
      </Box>

      {/* Прогресс-бар */}
      <LinearProgress
        variant="determinate"
        value={progressPercent}
        color={allScanned ? "success" : "primary"}
        sx={{
          height: 6,
          bgcolor: '#e8eaf6',
          '& .MuiLinearProgress-bar': {
            transition: 'transform 0.5s ease-in-out'
          }
        }}
      />

      {/* Список коробов - карточки */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, position: 'relative' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {boxes.map((box) => (
            <Box
              key={box.box_num}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: box.scanned
                  ? 'success.light'
                  : (lastScanned === box.box_num ? 'success.main' : colors.border),
                bgcolor: box.scanned ? '#f0fdf4' : 'white',
                borderLeftWidth: 4,
                borderLeftColor: box.scanned ? 'success.main' : 'grey.300',
                transition: 'all 0.3s ease',
                ...(lastScanned === box.box_num && {
                  boxShadow: '0 0 0 2px rgba(76,175,80,0.3)',
                  transform: 'scale(1.01)'
                })
              }}
            >
              {/* Иконка статуса */}
              {box.scanned ? (
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
              ) : (
                <UncheckedIcon sx={{ color: 'grey.400', fontSize: 24 }} />
              )}

              {/* Номер */}
              <Typography
                variant="body1"
                sx={{
                  fontWeight: box.scanned ? 700 : 500,
                  color: box.scanned ? 'success.dark' : 'text.primary',
                  flex: 1
                }}
              >
                Короб №{box.box_num}
              </Typography>

              {/* Время */}
              {box.scanned && box.scannedAt ? (
                <Chip
                  label={new Date(box.scannedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                />
              ) : (
                <Typography variant="caption" color="text.disabled">
                  Ожидает сканирования
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Ошибка в виде всплывающего блока снизу */}
        {error && (
          <Box sx={{ m: 2, position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 9999 }}>
            <Alert severity="error" onClose={() => setError('')}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Ошибка сканирования:
              </Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
            <Box sx={{ mt: 1, borderTop: `1px solid ${colors.border}`, backgroundColor: colors.warning, p: 2, borderRadius: 1, color: 'white', minWidth: '100%' }}>
              <Typography variant="body2">Возможные причины: либо зажата клавиша CapsLock, либо язык системы не RU/ENG, либо нужно повторно отсканировать код.</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Футер */}
      {showFooter && (
        <Box sx={{
          p: 2,
          borderTop: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'white',
          flexShrink: 0
        }}>
          <Button
            variant="outlined"
            onClick={onBack}
          >
            Назад
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {progress.scanned} из {progress.total}
            </Typography>
            <Button
              variant="contained"
              color="success"
              onClick={onComplete}
              disabled={!allScanned}
              startIcon={<CheckIcon />}
              sx={{
                px: 3,
                fontWeight: 'bold',
                boxShadow: allScanned ? 4 : 0,
                ...(allScanned && {
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(76,175,80,0.4)' },
                    '70%': { boxShadow: '0 0 0 10px rgba(76,175,80,0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(76,175,80,0)' },
                  }
                })
              }}
            >
              {allScanned ? 'Завершить' : `Осталось: ${progress.total - progress.scanned}`}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BoxScanningView;
